<template>
  <div>
    <v-dialog
      scrollable
      persistent
      overlayOpacity="0.85"
      :modelValue="editVariantDialog"
      width="95%"
    >
      <v-card v-if="model.actions" class="experiment-create-variant-dialog">
        <v-card-title class="headline experiment-create-variant flex justify-between">
          {{ title }}
          <RfTestEmailSender v-if="isEmail" :path="path" :actionGroupId="variantDetail.id" />
          <v-btn
            v-else-if="isStandaloneWeb"
            style="width: 150px"
            color="#efefef"
            variant="flat"
            @click="() => domainChooseDialog?.open()"
          >
            <v-icon start>md:open_in_new</v-icon>Live Preview
          </v-btn>
        </v-card-title>
        <v-card-text style="padding: 0">
          <div
            class="rf-detail-content-wrapper abtest-variant-modal-preview"
            style="margin: 0; padding: 0 10px 10px"
          >
            <div class="create-variant-dialog-header">
              <label>Variation name:</label>
              <div class="variant-name">
                <v-text-field
                  v-model.trim="model.name"
                  variant="outlined"
                  persistentPlaceholder
                  density="compact"
                  solo
                  flat
                  hideDetails
                  label="Variation Name"
                  required
                  :disabled="userStore.isReadOnly || isControl"
                  hint="Name the variation. This path variation will be displayed as Test Path - Variation Name."
                  persistentHint
                  placeholder="e.g. option-1"
                  :rules="[rules.required]"
                ></v-text-field>
              </div>
            </div>
            <RfAlert
              v-if="currExperiment.is_active && currExperiment.is_live_editable && !isDisabled"
              type="warning"
              class="mb-4"
            >
              Changes to a live experiment could compromise the results.
            </RfAlert>
            <div style="display: flex; flex-direction: row">
              <div class="retention-editor-leftcol" :class="disabledClass">
                <RfRetentionActionsInputs
                  :actions="model.actions"
                  :model="path"
                  :backgroundImageEnabled="backgroundImageEnabled"
                  :viewTypeTab="viewTypeTab"
                  :actionGroup="model"
                  @forceRender="forceRender"
                />
              </div>
              <div class="retention-editor-rightcol">
                <div
                  v-if="isHorizontalVerticalTile"
                  class="rf-retention-modal-preview retention-tile-preview"
                  style="height: 100%; min-height: 700px; overflow: hidden"
                >
                  <RfRetentionTile
                    :key="previewKey"
                    ref="tile"
                    :actions="model.actions"
                    :model="path"
                    :viewTypeTab="viewTypeTab"
                    @clickTab="clickTab"
                  />
                </div>
                <div
                  v-if="path.path_type === 'text'"
                  class="rf-retention-modal-preview retention-text-preview"
                  style="height: 100%; min-height: 700px; overflow: hidden"
                >
                  <RfRetentionTileText
                    :key="previewKey"
                    ref="text"
                    :actions="model.actions"
                    :model="path"
                    :viewTypeTab="viewTypeTab"
                    @clickTab="clickTab"
                  />
                </div>
                <RfStandalonePreview
                  v-if="isStandalonePreview"
                  :key="previewKey"
                  ref="standalone"
                  :model="path"
                  :actions="model.actions"
                  :actionGroupId="model.id"
                  :viewTypeTab="viewTypeTab"
                  :webPreviewClass="abVariantPreviewClass"
                  @clickTab="clickTab"
                />
                <div v-if="isEmail">
                  <RfRetentionEmail
                    :key="previewKey"
                    ref="email"
                    :actions="model.actions"
                    :viewTypeTab="viewTypeTab"
                    :model="path"
                    @clickTab="clickTab"
                  />
                </div>
                <div v-if="isPushNotification">
                  <RfRetentionPush
                    :key="previewKey"
                    ref="scheduled_push"
                    :actions="model.actions"
                    :viewTypeTab="viewTypeTab"
                    :model="path"
                    @clickTab="clickTab"
                  />
                </div>
              </div>
            </div>
            <div class="variant-third-party-actions" :class="disabledClass" style="padding: 0">
              <v-expansion-panels v-model="getPromoThirdPartyActions" multiple focusable>
                <v-expansion-panel>
                  <v-expansion-panel-title>ACTIONS</v-expansion-panel-title>
                  <v-expansion-panel-text class="retention-config-segments">
                    <div class="api-website-action-div">
                      <div style="padding-left: 4px">
                        <RfRetentionActionsThirdParty
                          ref="thirdParty"
                          :actions="model.actions"
                          :model="path"
                          :action_group_connector_actions="model.action_group_connector_actions"
                          :disabled="false"
                          action_type="connectors"
                          :isControl="isControl"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-title>Triggers</v-expansion-panel-title>
                  <v-expansion-panel-text style="padding: 10px 20px; background: #f4f9fd">
                    <RfTrigger
                      ref="trigger"
                      :isDisabled="isDisabled || isControl"
                      :model="modelWithPathInfo"
                      :isEditBtnDisabled="isControl"
                    />
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            size="x-large"
            class="cancel-btn"
            variant="outlined"
            width="100px"
            @click="$emit('closeEditVariantModal')"
            >{{ isDisabled ? "Close" : "Cancel" }}</v-btn
          >
          <RfButton
            icon="md:save"
            buttonText="Save changes"
            size="x-large"
            color="primary"
            width="200px"
            :disabled="isDisabled"
            @click="saveExperiment"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <RfDomainChooserWrapper ref="domainChooseDialog" @open="openLivePreview" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";
import RfRetentionTile from "@/components/RfPathCreate/RfRetentionActions/RfRetentionTile.vue";
import RfRetentionTileText from "@/components/RfPathCreate/RfRetentionActions/RfRetentionTileText.vue";
import RfRetentionActionsInputs from "@/components/RfPathCreate/RfRetentionActions/RfRetentionActionsInputs.vue";
import RfRetentionActionsThirdParty from "@/components/RfPathCreate/RfRetentionActions/RfRetentionActionsThirdParty.vue";
import RfStandalonePreview from "@/components/RfPathCreate/RfRetentionDevices/RfStandalonePreview.vue";
import RfDomainChooserWrapper from "@/components/RfCommonCards/RfDomainChooserWrapper.vue";
import RfTrigger from "@/components/RfPathCreate/RfTrigger.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import { useLive } from "@/utils/composables/useLive";
import RfRetentionEmail from "@/components/RfPathCreate/RfRetentionActions/RfRetentionEmail.vue";
import RfRetentionPush from "@/components/RfPathCreate/RfRetentionActions/RfRetentionPush.vue";
import RfTestEmailSender from "@/components/RfPromotions/RfTestEmailSender.vue";
import { cloneDeep } from "lodash-es";
import { useToastsStore } from "@/pinia/toastsStore";
import {
  isEmail as isEmailFn,
  isStandaloneWeb as isStandaloneWebFn,
  isPushNotification as isPushNotificationFn,
} from "@/utils/prompts/promptHelpers";
import { useExperimentsStore } from "@/pinia/experimentsStore";
import { useActionsStore } from "@/pinia/actionsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useLiquidContextStore } from "@/pinia/liquidContextStore";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";
import RfAlert from "@/components/alerts/RfAlert.vue";

const props = defineProps({
  path: { type: Object, default: () => ({}) },
  editVariantDialog: { type: Boolean, default: false },
  currExperiment: { type: Object, default: () => ({}) },
  variantDetail: { type: Object, default: () => ({}) },
  variantName: { type: String, default: "" },
  isNew: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  title: { type: String, default: "" },
});

const emit = defineEmits(["closeEditVariantModal"]);

const toastsStore = useToastsStore();
const appsStore = useAppsStore();
const experimentsStore = useExperimentsStore();
const actionsStore = useActionsStore();
const userStore = useUserStore();
const live = useLive();
const liquidContextStore = useLiquidContextStore();

const domainChooseDialog = useTemplateRef("domainChooseDialog");
const getPromoThirdPartyActions = ref([0]);
const viewTypeTab = ref(isPushNotificationFn(props.path) ? "mobilewebViewTab" : "desktopViewTab");
const previewKey = ref(0);
const backgroundImageEnabled = ref(null);
const model = ref({});

const thirdParty = ref(null);
const trigger = ref(null);
const tile = ref(null);
const standalone = ref(null);
const text = ref(null);

const rules = {
  required: value => !!value || "Name field is required.",
};

const appClientActions = computed(() => actionsStore.clientActions || []);
const isDisabled = computed(() => userStore.isDisabledWithMessage || props.disabled);
const isControl = computed(() => model.value.is_control);
const isEmail = computed(() => isEmailFn(props.path));
const isPushNotification = computed(() => isPushNotificationFn(props.path));
const isStandaloneWeb = computed(() => isStandaloneWebFn(props.path));
const isHorizontalVerticalTile = computed(() =>
  [PromptTypes.horizontal.value, PromptTypes.vertical.value, PromptTypes.tile.value].includes(
    props.path.path_type,
  ),
);
const isStandalonePreview = computed(() =>
  [
    PromptTypes.retention_modal.value,
    PromptTypes.video.value,
    PromptTypes.interstitial.value,
    PromptTypes.widget.value,
    PromptTypes.bottom_banner.value,
  ].includes(props.path.path_type),
);
const abVariantPreviewClass = computed(() =>
  props.path.path_type === PromptTypes.video.value ? "retention-video-preview" : "",
);
const disabledClass = computed(() => {
  return isDisabled.value ? "variant-detail-actions inputs-disabled" : "";
});
const modelWithPathInfo = computed(() => {
  const result = cloneDeep(model.value);
  result.path_type = props.path.path_type;
  result.device_type = props.path.device_type;
  return result;
});

watch(
  () => props.editVariantDialog,
  v => {
    if (v) {
      if (Object.keys(props.variantDetail || {})?.length) {
        model.value = cloneDeep(props.variantDetail);
      } else {
        model.value = cloneDeep({
          name: null,
          traffic_percent: 0,
          actions: props.path.actions,
          action_group_connector_actions: props.path.action_group_connector_actions,
          triggers: props.path.triggers,
        });
      }
    }
  },
);

onMounted(() => {
  liquidContextStore.init();

  if (props.variantDetail) {
    model.value = cloneDeep(props.variantDetail);
  } else {
    model.value = cloneDeep({
      name: null,
      traffic_percent: 0,
      actions: props.path.actions,
      action_group_connector_actions: props.path.action_group_connector_actions,
      triggers: props.path.triggers,
    });
  }

  if (props.isNew) {
    backgroundImageEnabled.value = props.path.id ? !!props.path.actions.rf_settings_bg_image : true;
  } else {
    backgroundImageEnabled.value = model.value.id
      ? !!model.value.actions.rf_settings_bg_image
      : true;
  }

  if (!appClientActions.value.length) {
    actionsStore.getClientActions(appsStore.app.id);
  }
});

const forceRender = () => {
  previewKey.value += 1;
};

const saveExperiment = async () => {
  try {
    if (!model.value.name) throw new Error("Variant name can't be empty");
    if (Object.keys(model.value.actions).length === 0)
      throw new Error("At least one action needs to be added");

    const actionPayload = {
      actions: model.value.actions,
      device_type: props.path.device_type,
      path_type: props.path.path_type,
    };

    if (standalone.value) {
      await standalone.value.fillModel(actionPayload);
    }
    if (tile.value) {
      await tile.value.fillModel(actionPayload);
    }
    if (text.value) {
      await text.value.fillModel(actionPayload);
    }

    let loadingIndex = -1;
    if (props.isNew) {
      const newItem = {
        name: model.value.name,
        traffic_percent: 0,
        actions: { ...actionPayload.actions },
        triggers: props.path.triggers,
        translations: model.value.translations,
      };
      thirdParty.value.fillModel(newItem);
      if (trigger.value) trigger.value.fillModel(newItem);

      props.currExperiment.variations = [...props.currExperiment.variations, newItem];
      loadingIndex = props.currExperiment.variations.length - 1;
    } else {
      const currVariantIndex = props.currExperiment.variations.findIndex(
        item => item.id === model.value.id,
      );
      props.currExperiment.variations[currVariantIndex] = {
        ...props.currExperiment.variations[currVariantIndex],
        name: model.value.name,
        actions: { ...actionPayload.actions },
        translations: model.value.translations,
      };
      thirdParty.value.fillModel(props.currExperiment.variations[currVariantIndex]);
      if (trigger.value) trigger.value.fillModel(props.currExperiment.variations[currVariantIndex]);
      loadingIndex = currVariantIndex;
    }

    experimentsStore.updateExperiment({
      pathId: props.path.id,
      expId: props.currExperiment.id,
      modelExperiment: props.currExperiment,
      loadingIndex,
    });

    emit("closeEditVariantModal");
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  }
};

const clickTab = tabType => {
  viewTypeTab.value = tabType;
};

const openLivePreview = domain => {
  live.viewPreview(domain, { actionGroupId: model.value.id });
  domainChooseDialog.value?.close();
};
</script>

<style lang="scss">
.inputs-disabled {
  &:not(.variant-third-party-actions) :not(.inputs-disabled--ignore).v-expansion-panel-text,
  &.variant-third-party-actions .promo-design-group.promotion-actions-table {
    pointer-events: none;
    opacity: 0.5;
  }
}
</style>
